import type { QuestionGenerationRequestResult } from "./question-generation-observability";

type SavedGenerationDeck = {
  user_id?: string;
  cards_count?: number;
  generation_metadata?: {
    question_generation_attempt_id?: string;
    orchestration_modal_function_call_id?: string;
    question_generation_lifecycle?: { status?: string };
  };
};

async function readAcceptedDeck(deckId: string, userId: string): Promise<SavedGenerationDeck | null> {
  const [{ doc, getDoc, getFirestore }, { auth, db }, { DECK_COLLECTION_NAME }] = await Promise.all([
    import("firebase/firestore/lite"),
    import("./init-firebase"),
    import("../../constants/GlobalTypesAndEnumsAndConstants"),
  ]);
  if (auth.currentUser?.uid !== userId) return null;
  // Lite uses an independent REST read rather than the existing Listen channel.
  const snapshot = await getDoc(doc(getFirestore(db.app), DECK_COLLECTION_NAME, deckId));
  if (auth.currentUser?.uid !== userId || !snapshot.exists()) return null;
  return snapshot.data() as SavedGenerationDeck;
}

/** A lost POST response is not proof that the durable generation failed. */
export async function recoverAcceptedQuestionGeneration({
  body,
  readDeck = readAcceptedDeck,
  timeoutMs = 5_000,
}: {
  body: Record<string, unknown>;
  readDeck?: (deckId: string, userId: string) => Promise<SavedGenerationDeck | null>;
  timeoutMs?: number;
}): Promise<QuestionGenerationRequestResult | null> {
  const attemptId = body.generation_attempt_id;
  const userId = body.user_id;
  const hasStableAttemptId = typeof attemptId === "string" && attemptId.trim().length > 0;
  const hasSignedInUserId = typeof userId === "string" && userId.trim().length > 0;
  // Recovery needs both the exact attempt and its signed-in owner.
  if (!hasStableAttemptId || !hasSignedInUserId) {
    return null;
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      (async () => {
        // Matches core's build_idempotent_generation_deck_id. Never guess the
        // newest deck or use a cached snapshot from a different attempt.
        const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(attemptId));
        const deckId = btoa(String.fromCharCode(...new Uint8Array(digest)))
          .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "").slice(0, 20);
        const deck = await readDeck(deckId, userId);
        const metadata = deck?.generation_metadata;
        if (deck?.user_id !== userId || metadata?.question_generation_attempt_id !== attemptId) return null;

        const status = metadata.question_generation_lifecycle?.status;
        const hasStarted = ["orchestrating", "dispatched", "running", "first_content"].includes(status || "");
        // An admission-only placeholder may never have been dispatched. Do not
        // navigate into an endless wait, or conceal a terminal zero-card failure.
        if (!hasStarted && !(typeof deck.cards_count === "number" && deck.cards_count > 0)) return null;
        return {
          generatedDeckId: deckId,
          orchestrationModalFunctionCallId: metadata.orchestration_modal_function_call_id || null,
          reusedExistingGenerationState: null,
        };
      })(),
      new Promise<null>((resolve) => {
        timeoutId = setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
  } catch {
    // Offline, permission, crypto and missing-document failures preserve the
    // original transport error. Recovery is a bounded read, never a new job.
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
